
import { ApprovalFlow, ApprovalStageKey } from "../types/approval";
import { ApprovalRequest } from "../types/legalOps";
import { isLegal, isLisa, isIrene } from "../config/roles";

export type SummaryUserKey = "kevin" | "lisa" | "irene";

// Terminal statuses where no one needs to act
const TERMINAL_STATUSES = [
  'APPROVED', 
  'FULLY_APPROVED', 
  'REJECTED', 
  'RETURNED', 
  'LEGAL_RETURNED', 
  'PUBLISHED', 
  'COMPLETED', 
  'CANCELLED'
];

/**
 * Helper: Get current assignee name(s) for a request
 */
export function getCurrentAssignees(
  request: ApprovalRequest, 
  flow?: ApprovalFlow | null
): string[] {
  // If status is terminal, no one is assigned
  if (TERMINAL_STATUSES.includes(request.status)) {
    return [];
  }

  // 1. Use Flow if available
  if (flow && flow.currentStageKey) {
    switch (flow.currentStageKey) {
      case 'CO_REVIEW': return ["相關部門會辦"];
      case 'LEGAL_REVIEW': return ["Kevin (法務)"];
      case 'MANAGER_LISA': return ["Lisa (主管)"];
      case 'MANAGER_IRENE': return ["Irene (主管)"];
      default: return [];
    }
  }

  // 2. Fallback based on Status fields
  if (request.status === 'SUBMITTED' || request.status === 'IN_REVIEW' || request.status === 'PENDING_LEGAL') {
    return ["Kevin (法務)"];
  }
  
  if (request.status === 'WAITING_APPROVAL' || request.status === 'PENDING_MANAGER') {
    // Check Lisa
    const lisaDone = request.lisaDecision === 'APPROVED' || request.managerLisaDecision === 'APPROVED';
    if (!lisaDone) return ["Lisa (主管)"];
    
    // Check Irene
    const ireneDone = request.ireneDecision === 'APPROVED' || request.managerIreneDecision === 'APPROVED';
    if (!ireneDone) return ["Irene (主管)"];
  }

  return ["處理中"];
}

/**
 * Determines if a specific approval request is currently pending for the given user.
 */
export function isApprovalPendingForUser(
  request: ApprovalRequest,
  userKey: SummaryUserKey,
  flow?: ApprovalFlow | null
): boolean {
  // 1. Filter out closed/terminal statuses immediately
  if (TERMINAL_STATUSES.includes(request.status)) {
    return false;
  }

  // 2. Logic using ApprovalFlow (Preferred)
  if (flow) {
    if (flow.status === 'COMPLETED' || flow.status === 'REJECTED') return false;

    const stage = flow.currentStageKey;
    if (!stage) return false;

    switch (userKey) {
      case "kevin":
        // Kevin (Legal) handles LEGAL_REVIEW
        return stage === 'LEGAL_REVIEW';

      case "lisa":
        return stage === 'MANAGER_LISA';

      case "irene":
        return stage === 'MANAGER_IRENE';
        
      default:
        return false;
    }
  }

  // 3. Fallback Logic using ApprovalRequest Fields (Legacy/No Flow)
  
  // KEVIN (Legal)
  if (userKey === "kevin") {
    return ['SUBMITTED', 'IN_REVIEW', 'PENDING_LEGAL'].includes(request.status);
  }

  // LISA (Manager 1)
  if (userKey === "lisa") {
    if (request.status === 'WAITING_APPROVAL' || request.status === 'PENDING_MANAGER') {
      // If already approved by Lisa, not pending her
      if (request.lisaDecision === 'APPROVED' || request.managerLisaDecision === 'APPROVED') {
        return false; 
      }
      return true;
    }
    return false;
  }

  // IRENE (Manager 2)
  if (userKey === "irene") {
    if (request.status === 'WAITING_APPROVAL' || request.status === 'PENDING_MANAGER') {
      // Must be approved by Lisa first
      const lisaDone = request.lisaDecision === 'APPROVED' || request.managerLisaDecision === 'APPROVED';
      const ireneDone = request.ireneDecision === 'APPROVED' || request.managerIreneDecision === 'APPROVED';
      return lisaDone && !ireneDone;
    }
    return false;
  }

  return false;
}
